import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../../css/bulk.scss";
import Header from "../common/Header";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";
import { sendMessage } from "../../apis/SendMessageAPI";
import { buildBulkQuoteMessage } from "../../utils/footerInquiryMessage";

const hasRichHtml = (s) =>
  typeof s === "string" && /<\/?(strong|b|em|i|ul|ol|li|br|a|sup)\b/i.test(s);

const renderSectionBody = (sec) => (
  <>
    {(sec.paragraphs || []).map((p, i) =>
      hasRichHtml(String(p)) ? (
        <p
          key={`p-${i}`}
          className="bulk__p"
          dangerouslySetInnerHTML={{ __html: p }}
        />
      ) : (
        <p key={`p-${i}`} className="bulk__p">
          {p}
        </p>
      )
    )}
    {Array.isArray(sec.list) && sec.list.length > 0 && (
      <ul className="bulk__list bulk__list--check">
        {sec.list.map((li, j) => (
          <li key={`li-${j}`} dangerouslySetInnerHTML={{ __html: li }} />
        ))}
      </ul>
    )}
    {Array.isArray(sec.ordered) && sec.ordered.length > 0 && (
      <ol className="bulk__steps">
        {sec.ordered.map((li, j) => (
          <li key={`ol-${j}`}>
            <span>{li}</span>
          </li>
        ))}
      </ol>
    )}
    {Array.isArray(sec.tail) &&
      sec.tail.map((p, i) =>
        hasRichHtml(String(p)) ? (
          <p
            key={`tail-${i}`}
            className="bulk__p bulk__p--tail"
            dangerouslySetInnerHTML={{ __html: p }}
          />
        ) : (
          <p key={`tail-${i}`} className="bulk__p bulk__p--tail">
            {p}
          </p>
        )
      )}
  </>
);

const Bulk = () => {
  const { t } = useTranslation();
  const sections = t("bulk.sections", { returnObjects: true }) || [];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [request, setRequest] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: null, text: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: null, text: "" });
    setSubmitting(true);
    try {
      const text = buildBulkQuoteMessage(name, email, request);
      await sendMessage(text);
      setFeedback({ type: "success", text: t("bulk.form.submitSuccess") });
      setName("");
      setEmail("");
      setRequest("");
    } catch (err) {
      console.error(err);
      setFeedback({ type: "error", text: t("bulk.form.submitError") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bulk-page">
      <Header />
      <main className="bulk__container">
        <div className="bulk__layout">
          <div className="bulk__main">
            <nav className="bulk__breadcrumb" aria-label="Breadcrumb">
              <Link to="/">{t("bulk.breadcrumbHome")}</Link>
            </nav>
            <h1 className="bulk__title">{t("bulk.title")}</h1>
            <p className="bulk__intro">{t("bulk.intro")}</p>

            {sections.map((sec, idx) => (
              <section className="bulk-sec" key={`b-${idx}`}>
                <h2 className="bulk-sec__title">{sec.title}</h2>
                {sec.boxed ? (
                  <div className="bulk-discount-box">
                    {renderSectionBody(sec)}
                  </div>
                ) : (
                  renderSectionBody(sec)
                )}
              </section>
            ))}
          </div>

          <aside className="bulk__aside" aria-label={t("bulk.form.title")}>
            <form className="bulk-form" onSubmit={handleSubmit} noValidate>
              <h2 className="bulk-form__title">{t("bulk.form.title")}</h2>

              <label className="bulk-form__label" htmlFor="bulk-name">
                {t("bulk.form.nameLabel")}
                <span className="bulk-form__req" aria-hidden="true">
                  {" "}
                  {t("bulk.form.requiredStar")}
                </span>
              </label>
              <input
                id="bulk-name"
                className="bulk-form__input"
                type="text"
                name="name"
                autoComplete="name"
                placeholder={t("bulk.form.namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />

              <label className="bulk-form__label" htmlFor="bulk-email">
                {t("bulk.form.emailLabel")}
                <span className="bulk-form__req" aria-hidden="true">
                  {" "}
                  {t("bulk.form.requiredStar")}
                </span>
              </label>
              <input
                id="bulk-email"
                className="bulk-form__input"
                type="email"
                name="email"
                autoComplete="email"
                placeholder={t("bulk.form.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <label className="bulk-form__label" htmlFor="bulk-request">
                {t("bulk.form.requestLabel")}
                <span className="bulk-form__req" aria-hidden="true">
                  {" "}
                  {t("bulk.form.requiredStar")}
                </span>
              </label>
              <textarea
                id="bulk-request"
                className="bulk-form__textarea"
                name="request"
                rows={6}
                placeholder={t("bulk.form.requestPlaceholder")}
                value={request}
                onChange={(e) => setRequest(e.target.value)}
                required
              />

              {feedback.text ? (
                <p
                  className={`bulk-form__feedback bulk-form__feedback--${feedback.type}`}
                  role="status"
                >
                  {feedback.text}
                </p>
              ) : null}

              <button
                className="bulk-form__submit"
                type="submit"
                disabled={submitting}
                aria-busy={submitting || undefined}
              >
                {submitting ? t("bulk.form.submitting") : t("bulk.form.submit")}
              </button>
            </form>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Bulk;
